import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sunrise, Send, Slack, Mail, CalendarClock, AlertTriangle, PenLine, Target,
  ArrowUpRight, CheckCircle2, Radio, Loader2, History, Clock,
} from "lucide-react";
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLE = {
  sent: "bg-pos-bg text-pos-text",
  simulated: "bg-neu-bg text-neu-text",
  error: "bg-neg-bg text-neg-text",
};

const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`;

export default function Briefings() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const { data, isLoading } = useQuery({
    queryKey: ["briefingPreview"],
    queryFn: () => api.get("/briefing/preview", { params: { app_base_url: origin } }).then((r) => r.data),
  });
  const { data: history } = useQuery({ queryKey: ["briefingHistory"], queryFn: () => api.get("/briefing/history").then((r) => r.data) });

  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/briefing/send", { app_base_url: origin });
      const summary = res.results.map((r) => `${r.channel}: ${r.status}`).join(" · ");
      toast.success(`Briefing sent: ${summary}`);
      qc.invalidateQueries({ queryKey: ["briefingHistory"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const testSlack = async () => {
    setTesting(true);
    try {
      const { data: res } = await api.post("/briefing/test-slack");
      if (res.status === "sent") toast.success("Test post delivered, check your Slack channel");
      else if (res.status === "simulated") toast.info(res.detail);
      else toast.error(res.detail);
    } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };

  const saveConfig = async (patch) => {
    try {
      await api.put("/briefing/config", patch);
      qc.invalidateQueries({ queryKey: ["briefingPreview"] });
      toast.success(patch.auto_send === false ? "Automatic briefing off" : "Automatic briefing updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading || !data) return <Loader label="Compiling this morning's briefing…" />;
  const b = data.briefing;
  const cfg = data.config || {};

  return (
    <div>
      <PageHeader
        title="Morning Briefing"
        subtitle="A daily digest of what needs your attention: negative signals, drafts to approve, and GOTV progress, with one-tap links into TL-Thunderbird."
        icon={Sunrise}
        actions={
          hasRole("admin", "manager") && (
            <Button onClick={send} disabled={sending} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="send-briefing-btn">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send today's briefing
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* Live preview */}
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm" data-testid="briefing-preview">
          <div className="flex items-center justify-between bg-brand-navy px-6 py-4 text-white">
            <div>
              <p className="font-heading text-lg font-bold">☀ TL-Thunderbird Morning Briefing</p>
              <p className="text-sm text-white/70">{b.pretty_date}</p>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold">
              <CalendarClock className="h-4 w-4" /> {b.days_to_eday} days to E-Day
            </span>
          </div>

          <div className="space-y-5 p-6">
            <Section icon={Radio} title={`Feed: ${b.sentiment.negative} negative of ${b.sentiment.total} signals`} link={b.links.feed} linkLabel="Respond in Feed">
              {b.top_negatives.length ? (
                <ul className="space-y-1.5">
                  {b.top_negatives.map((n) => (
                    <li key={n.id} className="flex items-start gap-2 text-sm text-brand-ink">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-red" />
                      <span><span className="font-medium">{n.title}</span> <span className="text-brand-mute">· {n.geo}</span></span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-pos-text">No urgent negatives right now.</p>}
            </Section>

            <div className="grid grid-cols-2 gap-3">
              <MiniCard icon={PenLine} label="Drafts pending" value={b.drafts_pending} to="/studio" />
              <MiniCard icon={AlertTriangle} label="Review queue" value={b.review_queue} to="/automations" />
            </div>

            <Section icon={Target} title={`GOTV: ${b.gotv.contacted}/${b.gotv.universe} contacted (${b.gotv.coverage}%)`} link={b.links.gotv} linkLabel="Open GOTV board">
              <p className="text-sm text-brand-mute">Goal progress {b.gotv.goal_progress}% toward {b.gotv.goal.toLocaleString()} contacts.</p>
              <ul className="mt-2 space-y-1.5">
                {b.priority_precincts.map((p) => (
                  <li key={p.precinct} className="text-sm text-brand-ink">
                    Knock <span className="font-semibold">{p.precinct}</span>: {p.contacted}/{p.universe} ({p.coverage}%)
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        </div>

        {/* Channels + history */}
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <h3 className="font-heading text-base font-bold text-brand-ink">Delivery channels</h3>
            <div className="mt-3 space-y-2.5">
              <Channel icon={Slack} name="Slack" connected={data.slack_connected}
                note={data.slack_connected ? "Connected: real delivery" : "Add an Incoming Webhook to enable"} />
              <Channel icon={Mail} name="Email (SendGrid)" connected={false} simulated
                note="Simulated until a SendGrid key is added" />
            </div>
            {data.slack_connected && hasRole("admin", "manager") && (
              <Button variant="outline" size="sm" className="mt-3 w-full" onClick={testSlack} disabled={testing} data-testid="slack-test-btn">
                {testing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Slack className="mr-1.5 h-3.5 w-3.5" />} Send test post to Slack
              </Button>
            )}
            {!data.slack_connected && (
              <Button asChild variant="outline" size="sm" className="mt-3 w-full">
                <Link to="/settings?tab=integrations" data-testid="connect-slack-link"><Slack className="mr-1.5 h-3.5 w-3.5" /> Connect Slack webhook</Link>
              </Button>
            )}
          </div>

          <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="auto-send-card">
            <h3 className="flex items-center gap-1.5 font-heading text-base font-bold text-brand-ink"><Clock className="h-4 w-4" /> Automatic delivery</h3>
            <p className="mt-1 text-xs text-brand-mute">
              Sends itself every morning to the channels above, {data.timezone || "America/Detroit"} time, once per day.
            </p>
            <div className="mt-3 flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <span className="text-sm font-medium text-brand-ink">Send automatically</span>
              <Switch checked={!!cfg.auto_send} disabled={!hasRole("admin", "manager")}
                onCheckedChange={(v) => saveConfig({ auto_send: v })} data-testid="auto-send-toggle" />
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
              <span className="text-sm font-medium text-brand-ink">Send at</span>
              <Select value={String(cfg.send_hour ?? 7)} disabled={!hasRole("admin", "manager") || !cfg.auto_send}
                onValueChange={(v) => saveConfig({ send_hour: Number(v) })}>
                <SelectTrigger className="w-[130px]" data-testid="send-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {cfg.auto_send && (
              <p className="mt-2.5 text-xs font-medium text-pos-text" data-testid="auto-send-status">
                Next briefing goes out at {hourLabel(cfg.send_hour ?? 7)}
                {data.slack_connected ? " to Slack" : " (Slack not connected, it will run in preview mode)"}.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
            <h3 className="flex items-center gap-1.5 font-heading text-base font-bold text-brand-ink"><History className="h-4 w-4" /> Recent briefings</h3>
            <div className="mt-3 space-y-2.5">
              {!history?.length && <p className="text-sm text-brand-mute">No briefings sent yet.</p>}
              {history?.map((h) => (
                <div key={h.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-brand-ink">{h.date}</span>
                    <span className="text-xs text-brand-mute">{timeAgo(h.created_at)}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {h.auto && <Badge variant="outline" data-testid={`auto-badge-${h.id}`}>auto</Badge>}
                    {h.results.map((r, i) => (
                      <Badge key={i} className={STATUS_STYLE[r.status] || "bg-slate-100"}>{r.channel}: {r.status}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, link, linkLabel }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-2 font-heading text-sm font-bold text-brand-navy"><Icon className="h-4 w-4" /> {title}</h4>
        {link && <a href={link} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-red hover:underline">{linkLabel} <ArrowUpRight className="h-3 w-3" /></a>}
      </div>
      {children}
    </div>
  );
}

function MiniCard({ icon: Icon, label, value, to }) {
  return (
    <Link to={to} className="rounded-lg border border-border p-3 transition-colors hover:bg-slate-50">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-brand-mute">{label}</span>
        <Icon className="h-4 w-4 text-brand-navy" />
      </div>
      <p className="mt-1 font-heading text-2xl font-extrabold text-brand-ink">{value}</p>
    </Link>
  );
}

function Channel({ icon: Icon, name, connected, simulated, note }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
      <span className="flex items-center gap-2 text-sm font-medium text-brand-ink"><Icon className="h-4 w-4" /> {name}</span>
      {connected ? (
        <Badge className="bg-pos-bg text-pos-text"><CheckCircle2 className="mr-1 h-3 w-3" /> Connected</Badge>
      ) : (
        <Badge className={simulated ? "bg-neu-bg text-neu-text" : "bg-slate-100 text-slate-500"} title={note}>{simulated ? "Simulated" : "Not connected"}</Badge>
      )}
    </div>
  );
}
